import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { AuthService, LegalInfo } from '@app/modules/auth/services/auth.service'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { EventService } from '@app/modules/events/services/event.service'
import { LandingPageOptions } from '@app/modules/personas/interfaces/persona-landing-option.interface'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { PersonaFactoryService } from '@app/modules/personas/models/persona.model'
import { Subject, forkJoin } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { EventLandingService } from '../../services/event-landing.service'
import { LandingPreviewerComponent } from '../landing-previewer/landing-previewer.component'
import { LandingSideNavComponent } from '../landing-side-nav/landing-side-nav.component'

@Component({
  selector: 'ngx-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  standalone: true,
  imports: [CommonModule, LandingSideNavComponent, LandingPreviewerComponent],
  providers: [EventService]
})
export class LandingPageComponent implements OnInit, OnDestroy {
  @Input() event_id: number = this.activatedRoute.snapshot.params.event_id
  @Input() persona_id: number = this.activatedRoute.snapshot.params.persona_id
  event: ServerRepresentedEvent
  persona: Persona
  isLoading = true
  hasErrors: boolean
  private _destroy$ = new Subject()

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Initialize Component
   * @param activatedRoute ActivatedRoute
   * @param eventService EventService
   * @param landingService LandingService
   * @param auth AuthService
   * @param personaService PersonaService
   */
  constructor(
    private activatedRoute: ActivatedRoute,
    private eventService: EventService,
    private landingService: EventLandingService,
    private auth: AuthService,
    private personaService: PersonaFactoryService
  ) {}

  /**
   * Initialize component
   * @returns void
   */
  ngOnInit(): void {
    this.getPersonaDetails()
  }

  /**
   * Get all forms for a specific event and filter them to get a specific form
   * Gets all legal info for organization and concats it to landing_page_options
   * Emits value to subject to pass it to two difference components
   * @returns void
   */
  private getPersonaDetails(): void {
    const personaInfo$ = this.eventService.getEventForms(this.event_id)
    const organizationInfo$ = this.auth.getOrganizationInfo(this.auth.currentWorkspace)
    const eventDetails$ = this.eventService.getEventInfo(this.event_id)

    forkJoin([personaInfo$, organizationInfo$, eventDetails$])
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: ([personas, workspaceInfo, eventDetails]) => {
          this.event = eventDetails
          this.persona = this.getPersona(personas)
          const options = this.getOptions(this.persona)
          const contactInfo = this.getContactInfo(workspaceInfo, eventDetails)

          if (!options.contacts.contactsList.length || options.contacts.contactsList[0].contactInfo.company_name !== this.auth.currentWorkspace.name) {
            options.contacts.contactsList = [{ isEnabled: true, contactInfo: { company_name: this.auth.currentWorkspace.name } }]
            this.landingService.updateLandingOptionsLocally(options)
          }

          this.landingService.contactsList = {
            isEnabled: true,
            contactInfo: { ...contactInfo, company_name: this.auth.currentWorkspace.name }
          }
          this.landingService.landingOptions$.next(options)
          this.landingService.updateCurrentPersona(this.persona)
          this.isLoading = false
        },
        error: () => {
          this.hasErrors = true
          this.isLoading = false
        }
      })
  }

  /**
   * Returns the persona with the matching persona id.
   * @param personas - An array of personas to search through.
   * @returns The found persona or undefined if no match is found.
   * @private
   */
  private getPersona(personas: Persona[]): Persona {
    return personas.find((persona) => persona.id == this.persona_id)
  }

  /**
   * Returns the landing page options for the given persona.
   * @param persona - The persona to get the landing page options for.
   * @returns The landing page options for the given persona.
   * @private
   */
  private getOptions(persona: Persona): LandingPageOptions {
    if (!persona?.landing_page_options) {
      return this.personaService.generateLandingPageOptions()
    }

    return persona.landing_page_options
  }

  /**
   * Returns the contact information for the given workspace and event details.
   * @param workspaceInfo - The workspace information.
   * @param eventDetails - The event details.
   * @returns The contact information.
   * @private
   */
  private getContactInfo(workspaceInfo: LegalInfo, eventDetails: ServerRepresentedEvent): Partial<LegalInfo> {
    const { company_name, company_address, country } = workspaceInfo
    const { company_phone, company_email } = { company_phone: this.auth.currentWorkspace.contact_number, company_email: this.auth.currentWorkspace.contact_email }

    return {
      company_name,
      company_phone,
      company_email,
      company_address,
      country,
      company_logo: eventDetails.workspace.logo
    }
  }

  /**
   * Updates the persona's public description
   * @param description - The new description to set
   */
  onDescriptionChange(description: string): void {
    if (this.persona) {
      this.persona.public_description = description
      this.landingService.updateCurrentPersona(this.persona)
    }
  }
}
